import { useCallback, useEffect, useState } from "react";
import { Sparkles, Loader2, TrendingDown, HardDrive, CalendarDays, Images, PiggyBank, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api } from "@/lib/api";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6", GREEN = "#1E7E45";

const MONTHS_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const monthLabel = (m) => {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  return `${MONTHS_DE[parseInt(mm, 10) - 1] || mm} ${y}`;
};

const fmtBytes = (b) => {
  if (!b || b <= 0) return "0 MB";
  const gb = b / 1073741824;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = b / 1048576;
  if (mb >= 1) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
};

export default function StorageOptimierung() {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loc, setLoc] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (loc) params.location_id = loc;
    return api.get("/optimization/report", { params })
      .then(({ data }) => setRep(data))
      .catch(() => toast.error("Report konnte nicht geladen werden."))
      .finally(() => setLoading(false));
  }, [loc]);

  useEffect(() => { load(); }, [load]);

  const requestUpgrade = async (addon) => {
    try { const { data } = await api.post("/storage/upgrade-request", { addon }); toast.success(data.message || "Deine Anfrage wurde erfasst. Wir melden uns."); setUpgradeOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen."); }
  };

  if (loading || !rep) {
    return (
      <CockpitLayout active="optimierung" title="Speicher & Optimierung">
        <div className="flex justify-center py-20"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
      </CockpitLayout>
    );
  }

  const cm = rep.current_month || {};
  const lm = rep.last_month || {};
  const at = rep.all_time || {};
  const adv = rep.storage_advantage || {};
  const chf = rep.chf || {};
  const advPct = Math.min(100, adv.saved_percent || 0);
  const hasHistory = (at.images || 0) > 0;

  const kpi = (icon, label, value, sub, accent) => (
    <div className="fundio-panel" style={{ padding: 16 }} data-testid={`kpi-${label}`}>
      <div className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{icon}{label}</div>
      <p className="mt-1.5 text-[22px] font-black tabular-nums" style={{ color: accent || INK }}>{value}</p>
      {sub && <p className="text-[12px]" style={{ color: MUTED }}>{sub}</p>}
    </div>
  );

  return (
    <CockpitLayout active="optimierung" title="Speicher & Optimierung">
      <div className="fundio-page-stack" data-testid="optimierung-page">
        {/* header */}
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" data-testid="optimierung-title">
              <Sparkles size={28} style={{ color: GOLD }} /> Speicher & Optimierung
            </h1>
            <p className="mt-1 fundio-muted text-[14px]" data-testid="optimierung-hint">{rep.hint}</p>
          </div>
          <button data-testid="upgrade-btn" onClick={() => setUpgradeOpen(true)}
            className="fundio-action" style={{ borderColor: GOLD, color: "var(--f-gold-text)" }}>
            Speicher erweitern
          </button>
        </div>

        {/* MULTI location filter */}
        {rep.multi && rep.locations?.length > 0 && (
          <div className="flex items-center gap-2" data-testid="location-filter-wrap">
            <MapPin size={14} style={{ color: SLATE }} />
            <select data-testid="optimierung-location-filter" value={loc} onChange={(e) => { setLoading(true); setLoc(e.target.value); }}
              className="fundio-input" style={{ minHeight: 40, width: "auto", padding: "6px 10px", fontSize: 13 }}>
              <option value="">Alle Standorte</option>
              {rep.locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.city ? ` · ${l.city}` : ""}</option>)}
            </select>
          </div>
        )}

        {/* this-month KPIs */}
        <div>
          <h2 className="flex items-center gap-2 text-[15px] font-bold" style={{ color: INK }}>
            <CalendarDays size={16} style={{ color: GOLD }} /> Dieser Monat <span className="text-[12px] font-medium" style={{ color: SLATE }}>· {monthLabel(cm.month)}</span>
          </h2>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="kpi-grid">
            {kpi(<Images size={13} />, "Bilder", cm.images ?? 0, "optimiert")}
            {kpi(<HardDrive size={13} />, "Original", fmtBytes(cm.original_bytes), "hochgeladen")}
            {kpi(<HardDrive size={13} />, "Optimiert", fmtBytes(cm.optimized_bytes), "gespeichert")}
            {kpi(<TrendingDown size={13} />, "Gespart", fmtBytes(cm.saved_bytes), null, GREEN)}
            {kpi(<TrendingDown size={13} />, "Einsparung", `${cm.saved_percent ?? 0}%`, null, GREEN)}
            {kpi(<PiggyBank size={13} />, "Ø pro Bild", fmtBytes(cm.avg_saved_bytes), "gespart")}
          </div>
        </div>

        {/* storage advantage */}
        <div className="fundio-panel" data-testid="storage-advantage">
          <h2 className="flex items-center gap-2 text-[15px] font-bold" style={{ color: INK }}><HardDrive size={16} style={{ color: GOLD }} /> Speicher-Vorteil</h2>
          <p className="mt-1 text-[13px]" style={{ color: SLATE }}>Was deine aktuell gespeicherten Bilder ohne die FUNDIO-Optimierung belegen würden.</p>

          <div className="mt-4 space-y-3">
            <div>
              <div className="flex items-center justify-between text-[13px]"><span style={{ color: MUTED }}>Ohne Optimierung</span><span className="font-bold tabular-nums" style={{ color: INK }} data-testid="adv-without">{fmtBytes(adv.without_bytes)}</span></div>
              <div className="mt-1 h-2.5 overflow-hidden rounded-full" style={{ background: "#EFEDE6" }}><div className="h-full rounded-full" style={{ width: "100%", background: "#C9CCD2" }} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between text-[13px]"><span style={{ color: MUTED }}>Mit FUNDIO</span><span className="font-bold tabular-nums" style={{ color: GOLD }} data-testid="adv-with">{fmtBytes(adv.with_bytes)}</span></div>
              <div className="mt-1 h-2.5 overflow-hidden rounded-full" style={{ background: "#EFEDE6" }}><div className="h-full rounded-full transition-all" style={{ width: `${adv.without_bytes ? Math.max(2, Math.round(adv.with_bytes / adv.without_bytes * 100)) : 0}%`, background: GOLD }} /></div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg p-3.5" style={{ background: "rgba(30,126,69,.08)" }}>
            <TrendingDown size={18} style={{ color: GREEN }} />
            <span className="text-[15px] font-black" style={{ color: GREEN }} data-testid="adv-saved">{fmtBytes(adv.saved_bytes)} Speicher eingespart</span>
            <span className="rounded-full px-2 py-0.5 text-[12px] font-bold" style={{ background: "rgba(30,126,69,.14)", color: GREEN }}>{advPct}%</span>
            {chf.enabled && (
              <span className="ml-auto text-[13px] font-semibold" style={{ color: MUTED }} data-testid="adv-chf">≈ CHF {chf.saved_chf}.- / Monat gespart</span>
            )}
          </div>
          {chf.enabled && <p className="mt-2 text-[11.5px]" style={{ color: SLATE }}>Berechnet aus CHF {chf.cost_per_gb}.- pro GB · {((adv.saved_bytes || 0) / 1073741824).toFixed(2)} GB eingespart.</p>}
        </div>

        {/* history */}
        <div className="fundio-panel" data-testid="history-card">
          <h2 className="flex items-center gap-2 text-[15px] font-bold" style={{ color: INK }}><CalendarDays size={16} style={{ color: GOLD }} /> Historie</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {[["Aktueller Monat", cm], ["Letzter Monat", lm], ["Gesamter Zeitraum", at]].map(([label, k]) => (
              <div key={label} className="rounded-lg p-3.5" style={{ background: "#FAF8F3", border: `1px solid ${LINE}` }} data-testid={`history-${label}`}>
                <p className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{label}</p>
                <p className="mt-1 text-[18px] font-black tabular-nums" style={{ color: GREEN }}>{fmtBytes(k.saved_bytes)}</p>
                <p className="text-[12px]" style={{ color: MUTED }}>{k.images || 0} Bilder · {k.saved_percent || 0}% gespart</p>
              </div>
            ))}
          </div>

          {rep.months?.length > 0 ? (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="months-table">
                <thead>
                  <tr className="text-left" style={{ color: SLATE }}>
                    <th className="pb-2 pr-3 font-semibold">Monat</th>
                    <th className="pb-2 pr-3 text-right font-semibold">Bilder</th>
                    <th className="pb-2 pr-3 text-right font-semibold">Original</th>
                    <th className="pb-2 pr-3 text-right font-semibold">Optimiert</th>
                    <th className="pb-2 pr-3 text-right font-semibold">Gespart</th>
                    <th className="pb-2 text-right font-semibold">%</th>
                  </tr>
                </thead>
                <tbody>
                  {rep.months.map((m, i) => (
                    <tr key={m.month} data-testid={`month-row-${m.month}`} style={{ borderTop: `1px solid ${LINE}`, background: i % 2 ? "#FBFAF6" : "transparent" }}>
                      <td className="py-2 pr-3 font-semibold" style={{ color: INK }}>{monthLabel(m.month)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums" style={{ color: MUTED }}>{m.images}</td>
                      <td className="py-2 pr-3 text-right tabular-nums" style={{ color: MUTED }}>{fmtBytes(m.original_bytes)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums" style={{ color: MUTED }}>{fmtBytes(m.optimized_bytes)}</td>
                      <td className="py-2 pr-3 text-right font-semibold tabular-nums" style={{ color: GREEN }}>{fmtBytes(m.saved_bytes)}</td>
                      <td className="py-2 text-right font-semibold tabular-nums" style={{ color: GREEN }}>{m.saved_percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-5 rounded-lg border border-dashed py-8 text-center text-[13px]" style={{ borderColor: LINE, color: SLATE }} data-testid="history-empty">
              Noch keine Optimierungsdaten. Lade Bilder hoch – FUNDIO optimiert sie automatisch und misst die Einsparung.
            </p>
          )}
        </div>

        {/* MULTI per-location breakdown */}
        {rep.multi && rep.location_breakdown?.length > 0 && (
          <div className="fundio-panel" data-testid="location-breakdown">
            <h2 className="flex items-center gap-2 text-[15px] font-bold" style={{ color: INK }}><MapPin size={16} style={{ color: GOLD }} /> Nach Standort</h2>
            <p className="mt-1 text-[13px]" style={{ color: SLATE }}>Historisch erreichte Optimierung je Standort (gesamter Zeitraum).</p>
            <div className="mt-4 space-y-3">
              {rep.location_breakdown.map((l) => {
                const p = Math.min(100, l.saved_percent || 0);
                return (
                  <div key={l.location_id} data-testid={`loc-breakdown-${l.location_id}`}>
                    <div className="flex items-center justify-between text-[13.5px]">
                      <span className="font-semibold" style={{ color: INK }}>{l.name}{l.city ? ` · ${l.city}` : ""}</span>
                      <span style={{ color: MUTED }}>{l.images} Bilder · {fmtBytes(l.saved_bytes)} gespart · {l.saved_percent}%</span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full" style={{ background: "#EFEDE6" }}>
                      <div className="h-full rounded-full" style={{ width: `${p}%`, background: GREEN }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!hasHistory && (
          <p className="text-[12px]" style={{ color: SLATE }} data-testid="no-history-note">
            Werte basieren ausschliesslich auf echten Upload-Messwerten. Es werden keine Beispiel- oder Schätzwerte angezeigt.
          </p>
        )}
      </div>

      {/* upgrade dialog (request only — no checkout) */}
      {upgradeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="upgrade-dialog" onClick={() => setUpgradeOpen(false)}>
          <div className="fundio-panel w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-[18px] font-black" style={{ color: INK }}><Sparkles size={18} style={{ color: GOLD }} /> Speicher erweitern</h3>
              <button onClick={() => setUpgradeOpen(false)} data-testid="upgrade-dialog-close"><X size={18} style={{ color: SLATE }} /></button>
            </div>
            <p className="mt-2 text-[13px]" style={{ color: MUTED }}>Wähle eine Erweiterung. Keine automatische Buchung – wir bestätigen deine Anfrage.</p>
            <div className="mt-4 space-y-3">
              <button data-testid="upgrade-5gb-btn" onClick={() => requestUpgrade("extra_storage_5gb")} className="flex w-full items-center justify-between rounded-xl border p-4 text-left hover:bg-fundio-subtle" style={{ borderColor: LINE }}>
                <span className="font-bold" style={{ color: INK }}>+5 GB Speicher</span>
                <span className="font-semibold" style={{ color: GOLD }}>CHF 5.- / Monat</span>
              </button>
              <button data-testid="upgrade-25gb-btn" onClick={() => requestUpgrade("extra_storage_25gb")} className="flex w-full items-center justify-between rounded-xl border p-4 text-left hover:bg-fundio-subtle" style={{ borderColor: LINE }}>
                <span className="font-bold" style={{ color: INK }}>+25 GB Speicher</span>
                <span className="font-semibold" style={{ color: GOLD }}>CHF 15.- / Monat</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </CockpitLayout>
  );
}
